import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Module, IModule } from '../models/module.model';
import { ApiResponse, PaginatedResponse } from '../interfaces/api.interface';

export interface CreateModuleRequest {
  name: string;
  description?: string;
  isActive?: boolean;
  companyTypes?: string[];
  routePath?: string;
  icon?: string;
  sortOrder?: number;
}

export interface UpdateModuleRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  companyTypes?: string[];
  routePath?: string;
  icon?: string;
  sortOrder?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminModulesService {
  private baseUrl = '/api/modules';

  constructor(private http: HttpClient) {}

  /**
   * Get all modules (superadmin only)
   */
  getModules(): Observable<ApiResponse<Module[]>> {
    return this.http.get<ApiResponse<IModule[]>>(this.baseUrl).pipe(
      map(response => ({
        ...response,
        data: response.data?.map(m => new Module(m)) || []
      }))
    );
  }

  /**for-user
   * Get modules with pagination
   */
  getModulesPaginated(page = 1, limit = 100): Observable<PaginatedResponse<Module>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<PaginatedResponse<IModule>>(`${this.baseUrl}/for-user`, { params }).pipe(
      map(response => ({
        ...response,
        data: response.data?.map(m => new Module(m)) || []
      }))
    );
  }

  /**
   * Get module by ID
   */
  getModuleById(id: number): Observable<ApiResponse<Module | undefined>> {
    return this.http.get<ApiResponse<IModule>>(`${this.baseUrl}/${id}`).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Module(response.data) : undefined
      }))
    );
  }

  /**
   * Get user modules only
   */
  getUserModules(): Observable<ApiResponse<Module[]>> {
    return this.http.get<ApiResponse<IModule[]>>(`${this.baseUrl}/user`).pipe(
      map(response => ({
        ...response,
        data: response.data?.map(m => new Module(m)) || []
      }))
    );
  }

  /**
   * Get admin modules only
   */
  getAdminModules(): Observable<ApiResponse<Module[]>> {
    return this.http.get<ApiResponse<IModule[]>>(`${this.baseUrl}/admin`).pipe(
      map(response => ({
        ...response,
        data: response.data?.map(m => new Module(m)) || []
      }))
    );
  }

  /**
   * Get modules for current user (filtered by company type)
   */
  getModulesForUser(): Observable<ApiResponse<Module[]>> {
    return this.http.get<ApiResponse<IModule[]>>(`${this.baseUrl}/for-user`).pipe(
      map(response => ({
        ...response,
        data: response.data?.map(m => new Module(m)) || []
      }))
    );
  }

  /**
   * Create new module
   */
  createModule(module: CreateModuleRequest): Observable<ApiResponse<Module | undefined>> {
    return this.http.post<ApiResponse<IModule>>(this.baseUrl, module).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Module(response.data) : undefined
      }))
    );
  }

  /**
   * Update module
   */
  updateModule(id: number, module: UpdateModuleRequest): Observable<ApiResponse<Module | undefined>> {
    return this.http.put<ApiResponse<IModule>>(`${this.baseUrl}/${id}`, module).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Module(response.data) : undefined
      }))
    );
  }

  /**
   * Delete module
   */
  deleteModule(id: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }
}
